<template>
  <div id="book-time-main" v-cloak>
    <div class="bg-white">
      <!-- Show loader until cities request completes (and optional activity filtering finishes) -->
      <div
        class="padding-top-xs-1 padding-bottom-xs-1"
        v-if="!cities.loaded || cities.filteringByActivity"
      >
        <img
          class="loading-spinner-nk"
          src="/wp-content/themes/nordiskakliniken/dist/images/loading-spinner.svg"
        />
      </div>
      <!-- Loaded but no cities available -->
      <div
        v-if="cities.loaded && !cities.available.length"
        class="container padding-top-xs-1 padding-bottom-xs-1"
      >
        <div class="nk-booking-empty-cities">
          <strong>Inga bokningsbara städer hittades.</strong>
          <div style="margin-top: 6px;">
            Kontrollera att minst en <strong>Plats</strong> är:
            <br />- publicerad
            <br />- kopplad till en <strong>Stad</strong>
            <br />- och har <strong>Tillgänglig i bokning</strong> aktiverad
          </div>
        </div>
      </div>
      <!-- City radio buttons - shows when cities are loaded -->
      <!-- Hidden while cities are filtered by ?activity_id, so a city that is
           about to be filtered out can't be picked -->
      <div
        v-cloak
        v-if="cities.available.length > 1 && !cities.filteringByActivity"
        class="container padding-top-xs-1 city-selection-container"
      >
        <div class="row">
          <div class="col-xs-12">
            <fieldset class="nk-city-radios">
              <legend class="nk-city-radios-legend">
                <h3 class="margin-top-xs-0 margin-bottom-xs-0">Välj stad</h3>
              </legend>
              <label
                v-for="city in cities.available"
                :key="city.id"
                class="nk-city-radio"
                :class="{
                  'nk-city-radio-checked nk-button-light-nude':
                    isSelectedCity(city),
                }"
              >
                <input
                  type="radio"
                  name="nk-booking-city"
                  :value="city.id"
                  :checked="isSelectedCity(city)"
                  @change="selectCity(city)"
                />
                <span class="nk-city-radio-label">{{ city.name }}</span>
                <!-- Inline (not a background image) so it stays sharp at any size; same shape as dist/images/check.svg -->
                <svg
                  v-if="isSelectedCity(city)"
                  class="nk-city-radio-check"
                  viewBox="0 0 26 18"
                  aria-hidden="true"
                  focusable="false"
                >
                  <polyline
                    points="24.5 0.6 15.3 10.8 9 17.6 1 9"
                    fill="none"
                    stroke="currentColor"
                    stroke-linecap="square"
                  />
                </svg>
              </label>
            </fieldset>
          </div>
        </div>
      </div>
      <!-- City hint: "Ingen stad vald" until a city is chosen, then which city is shown -->
      <div
        v-if="cities.loaded && cities.available.length > 1 && !cities.filteringByActivity"
        class="container padding-top-xs-1 padding-bottom-xs-1"
      >
        <div class="row">
          <div class="col-xs-12 col-lg-6">
            <div class="nk-city-required-hint" aria-live="polite">
              <template v-if="cities.selected">
                Visar behandlingar för {{ cities.selected.name }}
              </template>
              <template v-else>Ingen stad vald</template>
            </div>
          </div>
        </div>
      </div>
      <!-- Debug helper (only when ?debug_booking=1) -->
      <div
        v-if="debugBooking"
        class="container padding-top-xs-1 padding-bottom-xs-1"
        style="opacity: 0.75; font-size: 12px;"
      >
        <div>
          <strong>Debug:</strong>
          Stad:
          <strong>{{ (cities.selected && cities.selected.name) ? cities.selected.name : '—' }}</strong>
          |
          location_id(s):
          <strong>{{ debugLocationIds }}</strong>
        </div>
        <div>
          Grupp-filter:
          <strong>{{ debugGroupFilter }}</strong>
        </div>
      </div>
      <!-- Show loading for activities after city is selected -->
      <div
        v-if="!activityGroups.bookable.length"
        class="container padding-top-xs-1 padding-bottom-xs-1"
      >
        <img
          class="loading-spinner-nk"
          src="/wp-content/themes/nordiskakliniken/dist/images/loading-spinner.svg"
        />
      </div>

      <div
        v-if="activityGroups.bookable.length && ui.loading.activityGroupsForCity"
        class="container padding-top-xs-1 padding-bottom-xs-1"
      >
        <img
          class="loading-spinner-nk"
          src="/wp-content/themes/nordiskakliniken/dist/images/loading-spinner.svg"
        />
      </div>

      <BookingModuleSearchActivity v-if="activityGroups.bookable.length" />
      <NextAvailableSlowToast />

      <div
        class="container hidden-xs hidden-sm"
        :class="{ 'nk-force-disable': loading.doingBooking }"
      >
        <div class="row">
          <!-- Treatment -->
          <!--<div class="col-xs-12 padding-top-xs-2 padding-bottom-xs-2">-->
          <div class="col-xs-12">
            <div class="padding-top-xs-0 padding-bottom-xs-0">
              <div class="fade-on-hover---">
                <span
                  v-for="activityGroup in filteredActivityGroups"
                  :key="activityGroup.activity_group_id"
                >
                  <a
                    v-if="activityGroups.selected != activityGroup"
                    href="#"
                    class="nk-button nk-button-margin link-plus-right"
                    @click.prevent="cities.selected && setActivityGroup(activityGroup)"
                    v-bind:class="{
                      'link-close-right':
                        activityGroups.selected == activityGroup,
                      'nk-button-inactive':
                        activityGroups.selected != activityGroup &&
                        activityGroups.selected,
                      'nk-button-light-nude':
                        !activityGroups.selected ||
                        activityGroups.selected == activityGroup,
                      'nk-button-disabled': !cities.selected,
                    }"
                    >{{ activityGroup.activity_group_name }}</a
                  >
                  <a
                    v-if="
                      activityGroups.selected == activityGroup &&
                      !activities.selected
                    "
                    href="#"
                    class="nk-button nk-button-margin link-plus-right"
                    @click.prevent="
                      cities.selected &&
                        (setActivityGroup(activityGroup), (activities.selected = false));
                    "
                    v-bind:class="{
                      'link-close-right':
                        activityGroups.selected == activityGroup,
                      'nk-button-inactive':
                        activityGroups.selected != activityGroup &&
                        activityGroups.selected,
                      'nk-button-light-nude':
                        activityGroups.selected == activityGroup,
                      'nk-button-disabled': !cities.selected,
                    }"
                    >{{ activityGroup.activity_group_name }}</a
                  >
                  <a
                    v-if="
                      activityGroups.selected == activityGroup &&
                      activities.selected
                    "
                    href="#"
                    class="nk-button nk-button-margin link-close-right nk-button-light-nude"
                    @click.prevent="activities.selected = false"
                    >{{ activities.selected.activity_name }}</a
                  >
                </span>
              </div>
            </div>
          </div>

          <!-- Activities -->
          <div
            v-if="activityGroups.selected && !activities.selected"
            class="col-xs-12"
          >
            <div class="hidden-xs hidden-sm">
              <ul
                class="nk-list nk-list-two-columns-md nk-treatments-list padding-top-xs-2"
              >
                <li v-for="activity in filteredActivities">
                  <a
                    href="#"
                    @click.prevent="setActivity(activity)"
                    class="force-icon-right"
                    v-bind:class="{
                      'link-plus-right': activities.selected == activity,
                      'text-gray':
                        activities.selected != activity && activities.selected,
                    }"
                    ><span
                      v-if="activityIsCampaign(activity)"
                      class="campaign-offer-icon"
                      ><img
                        src="/wp-content/themes/nordiskakliniken/dist/images/star-solid.svg"
                    /></span>
                    {{ activity.activity_name }}</a
                  >
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <BookingMobileButtons />
    </div>

    <div
      class="book-time-dates padding-bottom-xs-2 padding-bottom-md-4"
      :class="{
        'book-time-inactive': !activities.selected || ui.loading.times,
        'hidden-xs': !selectedSlot,
        'hidden-sm': !selectedSlot,
      }"
      id="book-time-dates"
    >
      <div class="container">
        <div class="hidden-xs hidden-sm">
          <BookingCalendarDesktop
            v-if="isActivityBookable(activities.selected.activity_id)"
          />
          <div
            class="row"
            v-if="!isActivityBookable(activities.selected.activity_id)"
          >
            <div class="col-xs-12 col-lg-6">
              <div class="alert alert-nk">
                <div
                  class="message-wrapper"
                  v-html="unbookableTreatmentMessage"
                ></div>
              </div>
            </div>
          </div>
          <div class="row">
            <PatientForm />
          </div>
        </div>
      </div>
    </div>

    <BookingMobileTimeSlots />
    <BookingMobileModalPatientForm />
    <BookingConfirmedModal />
    <BookingErrorModal />
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import BookingMobileButtons from './BookingMobileButtons.vue';
import BookingMobileTimeSlots from './BookingMobileTimeSlots.vue';
import BookingMobileModalPatientForm from './BookingMobileModalPatientForm.vue';
import PatientForm from './PatientForm.vue';
import BookingConfirmedModal from './BookingConfirmedModal.vue';
import BookingErrorModal from './BookingErrorModal.vue';
import BookingCalendarDesktop from './BookingCalendarDesktop.vue';
import BookingModuleSearchActivity from './BookingModuleSearchActivity.vue';
import NextAvailableSlowToast from './NextAvailableSlowToast.vue';

export default {
  components: {
    BookingMobileButtons,
    BookingMobileTimeSlots,
    BookingMobileModalPatientForm,
    PatientForm,
    BookingConfirmedModal,
    BookingErrorModal,
    BookingCalendarDesktop,
    BookingModuleSearchActivity,
    NextAvailableSlowToast,
  },
  computed: {
    ...mapState([
      'activityGroups',
      'activities',
      'providers',
      'selectedSlot',
      'dates',
      'ui',
      'booking',
      'patient',
      'unbookableTreatments',
      'unbookableTreatmentMessage',
      'cities',
    ]),
    ...mapGetters([
      'dateIsInThePast',
      'activityIsCampaign',
      'formatProviderName',
      'selectedSlotFormated',
      'formIsValid',
      'loading',
      'isActivityBookable',
      'filteredActivityGroups',
      'filteredActivities',
    ]),
    debugBooking() {
      try {
        const url = new URL(window.location.href);
        return url.searchParams.get('debug_booking') === '1';
      } catch (e) {
        return false;
      }
    },
    debugLocationIds() {
      try {
        const locs =
          this.cities && this.cities.selected && Array.isArray(this.cities.selected.locations)
            ? this.cities.selected.locations
            : [];
        const ids = (locs || []).map((l) => String(l.id)).filter(Boolean);
        return ids.length ? ids.join(', ') : '—';
      } catch (e) {
        return '—';
      }
    },
    debugGroupFilter() {
      try {
        const isLoading = !!(this.ui && this.ui.loading && this.ui.loading.activityGroupsForCity);
        const ids = this.activityGroups ? this.activityGroups.cityBookableGroupIds : null;
        const state =
          ids === null || typeof ids === 'undefined'
            ? 'inte aktivt (visar alla)'
            : Array.isArray(ids)
              ? `aktivt (${ids.length} group_ids${ids.length ? ': ' + ids.map((x) => String(x)).join(', ') : ''})`
              : 'okänt';
        return `${isLoading ? 'laddar' : 'klar'} • ${state}`;
      } catch (e) {
        return '—';
      }
    },
  },
  methods: {
    ...mapActions([
      'setActivityGroup',
      'setActivity',
      'setupActiveDates',
      'setSelectedSlot',
      'setProvider',
      'getRealWeekday',
      'setModalState',
      'handleSwipe',
      'shouldDeselectTimeslot',
      'setSelectedCity',
      'clearCitySelection',
    ]),

    // Select city from radio buttons. setSelectedCity commits the selection
    // synchronously; don't assign cities.selected first, or the action sees
    // no change and booking_city_changed never fires.
    selectCity(city) {
      this.setSelectedCity(city);
    },

    isSelectedCity(city) {
      return !!(this.cities.selected && this.cities.selected.id === city.id);
    },
  },
  created() {
    this.$store.dispatch('init', {
      vueInstance: this,
    });
  },

  mounted() {
    // Prefill cities from preloaded server data to avoid extra loading state
    try {
      const preloaded =
        typeof window !== 'undefined' &&
        window.profetWPVars &&
        Array.isArray(window.profetWPVars.contactInformation)
          ? window.profetWPVars.contactInformation
          : null;
      if (
        preloaded &&
        (!this.cities.available || this.cities.available.length === 0)
      ) {
        // Only prefill if the payload already contains locations (same shape as /nk/getBookingLocations/).
        // contactInformation is used by the contact page and may contain cities without any booking locations,
        // which would break city->location filtering in the booking module.
        const preloadedCities = preloaded
          .filter((c) => c && (c.id || c.slug) && c.name && Array.isArray(c.locations))
          .map((c) => ({
            id: c.id || c.slug,
            name: c.name,
            slug: c.slug || c.id,
            locations: c.locations,
          }));
        if (preloadedCities.length) {
          this.$store.commit('SET_AVAILABLE_CITIES', preloadedCities);
          this.$store.commit('SET_CITIES_LOADED', true);
          // If only one city exists, auto-select it (user otherwise cannot proceed).
          // programmatic: not a user choice — must not fire booking_city_changed.
          if (preloadedCities.length === 1) {
            this.setSelectedCity({ city: preloadedCities[0], programmatic: true });
          }
        }
      }
    } catch (e) {}
  },

  watch: {
    /* Watch for changes to the availableTimes object. This is necessary to make the slick slider work as intended */
    availableTimes(newTimes) {
      if (!this.$refs.slick) {
        return;
      }

      let currIndex = this.$refs.slick.currentSlide();
      this.$refs.slick.destroy();
      this.$nextTick(() => {
        this.$refs.slick.create();
        this.$refs.slick.goTo(currIndex, true);
      });
    },
    // Ensure Stockholm is selected as soon as cities load
    'cities.available'(list) {
      try {
        if (!list || !Array.isArray(list) || !list.length) {
          return;
        }
        const selected = this.cities && this.cities.selected ? this.cities.selected : null;

        // If we already have a selected city object but it's missing locations (old shape),
        // upgrade it to the version from the API response (with locations[]) so time filtering works.
        if (selected) {
          const match = list.find((c) => (c.id && selected.id && c.id === selected.id) || (c.slug && selected.slug && c.slug === selected.slug) || (c.name && selected.name && c.name === selected.name));
          if (match && Array.isArray(match.locations)) {
            this.cities.selected = match;
            // programmatic: shape upgrade of the already-selected city, not a user choice.
            this.setSelectedCity({ city: match, programmatic: true });
          }
          return;
        }
        // If only one city exists, auto-select it (user otherwise cannot proceed).
        // programmatic: not a user choice — must not fire booking_city_changed.
        if (list.length === 1) {
          this.cities.selected = list[0];
          this.setSelectedCity({ city: list[0], programmatic: true });
        }
      } catch (e) {}
    },
  },
};
</script>
